import React, { useState } from 'react';
import { Box, Typography, Tooltip, Grid as MuiGrid } from '@mui/material';
import {
  Clear as ClearIcon,
  LayersClear as LayersClearIcon,
  DeleteForever as DeleteForeverIcon,
  ModeEdit as LinePaintIcon,
  HighlightAlt as AreaPaintIcon,
  Terrain as TerrainIcon,
  TableRestaurant as ObjectsIcon,
  Construction as StructureIcon,
  Visibility as FogIcon,
  CleaningServices as EraseIcon,
} from '@mui/icons-material';
import {  PAINT_MODE, PAINT_CATEGORY, PAINT_COLORS, OBJECT_ITEMS, STRUCTURE_ITEMS, FOG_ITEMS, ERASE_ITEMS } from '../../data/paintObjects';
import { 
  ContextualToolbar, 
  SecondaryModeButton,
  ToolbarDivider,
  DangerIconButton,
  SelectionBox,
  SelectionPreview,
  SelectionText,
  SelectionMenuItem,
  SelectionMenuPreview,
  SelectionMenuText
} from '../shared/StyledComponents';
import { GGPopover } from '../shared/StyledDialogs';
import ToggleModeButtons from '../shared/ToggleModeButtons';


const PaintToolbar = ({
  paintMode,
  categoryMode,
  setPaintMode,
  setCategoryMode,
  selectedPaintColor,
  onPaintColorSelect,
  onClearColors,
  onClearGrid,
  showClearGrid,
  isCreator,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const isPopoverOpen = Boolean(anchorEl);
  const prevCategoryRef = React.useRef(categoryMode);

  const paintModes = [
    { value: PAINT_MODE.LINE, label: 'Line', icon: LinePaintIcon, tooltip: 'Line' },
    { value: PAINT_MODE.AREA, label: 'Area', icon: AreaPaintIcon, tooltip: 'Area' },
  ];

  // Get the current item list based on category
  const getCurrentItems = () => {
    switch (categoryMode) {
      case PAINT_CATEGORY.TERRAIN:
        return PAINT_COLORS;
      case PAINT_CATEGORY.OBJECTS:
        return OBJECT_ITEMS;
      case PAINT_CATEGORY.STRUCTURE:
        return STRUCTURE_ITEMS;
      case PAINT_CATEGORY.FOG:
        return FOG_ITEMS;
      case PAINT_CATEGORY.ERASE:
        return ERASE_ITEMS;
      default:
        return PAINT_COLORS;
    }
  };

  const currentItems = getCurrentItems();
  
  // Find the selected item
  const getSelectedItem = () => {
    if (!selectedPaintColor) {
      return currentItems[0];
    }
    
    // For string colors (terrain/objects)
    if (typeof selectedPaintColor === 'string') {
      const foundItem = currentItems.find(item => item.color === selectedPaintColor);
      return foundItem || currentItems[0];
    }
    
    // For object colors (tools)
    if (typeof selectedPaintColor === 'object') {
      const foundItem = currentItems.find(item => {
        if (typeof item.color !== 'object') return false;
        // Check if the color object matches
        return JSON.stringify(item.color) === JSON.stringify(selectedPaintColor);
      });
      return foundItem || currentItems[0];
    }
    
    return currentItems[0];
  };

  const selectedItem = getSelectedItem();

  // Auto-select first item when switching categories
  React.useEffect(() => {
    if (prevCategoryRef.current !== categoryMode) {
      prevCategoryRef.current = categoryMode;
      const items = getCurrentItems();
      
      if (items.length > 0) {
        onPaintColorSelect?.(items[0].color);
      }
    }
  }, [categoryMode, onPaintColorSelect]);

  const handlePreviewClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleItemSelect = (color) => {
    onPaintColorSelect?.(color);
    handlePopoverClose();
  };

  // Get the category label for display
  const getCategoryLabel = () => {
    switch (categoryMode) {
      case PAINT_CATEGORY.TERRAIN:
        return 'Terrain';
      case PAINT_CATEGORY.OBJECTS:
        return 'Object';
      case PAINT_CATEGORY.STRUCTURE:
        return 'Structure';
      case PAINT_CATEGORY.FOG:
        return 'Fog Tool';
      case PAINT_CATEGORY.ERASE:
        return 'Erase Tool';
      default:
        return 'Item';
    }
  };

  // Check if selected color matches item color (handles both string and object colors)
  const isColorSelected = (itemColor) => {
    if (typeof itemColor === 'string' && typeof selectedPaintColor === 'string') {
      return itemColor === selectedPaintColor;
    }
    if (typeof itemColor === 'object' && typeof selectedPaintColor === 'object') {
      return JSON.stringify(itemColor) === JSON.stringify(selectedPaintColor);
    }
    return false;
  };

  // Render tool preview based on type
  const renderToolPreview = (type, size = '28px') => {
    const styles = {
      width: size,
      height: size,
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };

    switch (type) {
      case 'eraseTerrain':
      case 'eraseObjects':
      case 'eraseWalls':
        return (
          <Box sx={{ ...styles, backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <ClearIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          </Box>
        );
      case 'wall':
        return (
          <Box sx={{ ...styles, backgroundColor: 'rgba(255,255,255,0.1)', position: 'relative' }}>
            <Box sx={{
              position: 'absolute',
              left: '10%',
              right: '10%',
              top: '46%',
              height: '3px',
              borderRadius: '2px',
              backgroundColor: '#000'
            }} />
          </Box>
        );
      case 'door':
        return (
          <Box sx={{
            ...styles,
            backgroundImage: `url(${selectedItem?.image || ''})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            border: '1px solid rgba(255,255,255,0.15)'
          }} />
        );
      case 'fog':
        return (
          <Box sx={{
            ...styles,
            backgroundColor: '#06090c',
            backgroundImage: `repeating-linear-gradient(
              45deg,
              #06090c,
              #06090c 5px,
              #1a1f25 5px,
              #1a1f25 6px
            )`,
            boxShadow: 'inset 0 0 8px rgba(255, 255, 255, 0.1)',
          }} />
        );
      case 'clearFog':
        return (
          <Box sx={{
            ...styles,
            backgroundColor: '#06090c',
            backgroundImage: `repeating-linear-gradient(
              45deg,
              #06090c,
              #06090c 5px,
              #1a1f25 5px,
              #1a1f25 6px
            )`,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom right, transparent calc(50% - 1px), rgba(255, 0, 0, 0.7) calc(50% - 1px), rgba(255, 0, 0, 0.7) calc(50% + 1px), transparent calc(50% + 1px))',
              pointerEvents: 'none',
            }
          }} />
        );
      default:
        return null;
    }
  };

  return (
    <ContextualToolbar>
      {/* Paint Mode Buttons */}
      <ToggleModeButtons
        modes={paintModes}
        activeMode={paintMode}
        onChange={setPaintMode}
      />

      <ToolbarDivider />

      <Tooltip title="Terrain" placement="bottom">
        <SecondaryModeButton
          active={categoryMode === PAINT_CATEGORY.TERRAIN}
          onClick={() => setCategoryMode?.(PAINT_CATEGORY.TERRAIN)}
        >
          <TerrainIcon />
        </SecondaryModeButton>
      </Tooltip>

      <Tooltip title="Objects" placement="bottom">
        <SecondaryModeButton
          active={categoryMode === PAINT_CATEGORY.OBJECTS}
          onClick={() => setCategoryMode?.(PAINT_CATEGORY.OBJECTS)}
        >
          <ObjectsIcon />
        </SecondaryModeButton>
      </Tooltip>

      <Tooltip title="Structure" placement="bottom">
        <SecondaryModeButton
          active={categoryMode === PAINT_CATEGORY.STRUCTURE}
          onClick={() => setCategoryMode?.(PAINT_CATEGORY.STRUCTURE)}
        >
          <StructureIcon />
        </SecondaryModeButton>
      </Tooltip>

      {isCreator && (
        <Tooltip title="Fog of War" placement="bottom">
          <SecondaryModeButton
            active={categoryMode === PAINT_CATEGORY.FOG}
            onClick={() => setCategoryMode?.(PAINT_CATEGORY.FOG)}
          >
            <FogIcon />
          </SecondaryModeButton>
        </Tooltip>
      )}

      <Tooltip title="Erase" placement="bottom">
        <SecondaryModeButton
          active={categoryMode === PAINT_CATEGORY.ERASE}
          onClick={() => setCategoryMode?.(PAINT_CATEGORY.ERASE)}
        >
          <EraseIcon />
        </SecondaryModeButton>
      </Tooltip>

      {/* Selected Item Preview with Popover */}
      <Tooltip title={`Click to change ${getCategoryLabel().toLowerCase()}`} placement="bottom">
        <SelectionBox onClick={handlePreviewClick} active={isPopoverOpen}>
          {selectedItem.image ? (
            <SelectionPreview sx={{ backgroundImage: `url(${selectedItem.image})` }} />
          ) : selectedItem.type ? (
            renderToolPreview(selectedItem.type, '28px')
          ) : (
            <SelectionPreview />
          )}
          <SelectionText>{selectedItem.name}</SelectionText>
        </SelectionBox>
      </Tooltip>

      {/* Popover Menu */}
      <GGPopover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 2,
              minWidth: '300px',
              maxWidth: '400px',
            }
          }
        }}
      >
        <Typography 
          sx={{ 
            mb: 1.5, 
            fontFamily: 'MedievalSharp',
            fontSize: '1rem',
            fontWeight: 600,
            color: 'text.primary'
          }}
        >
          Select {getCategoryLabel()}
        </Typography>
        <MuiGrid container spacing={1.5}>
          {currentItems.map(({ name, color, image, description, type }) => {
            const isToolCategory = categoryMode === PAINT_CATEGORY.FOG || categoryMode === PAINT_CATEGORY.ERASE || categoryMode === PAINT_CATEGORY.STRUCTURE;
            return (
              <MuiGrid item xs={isToolCategory ? 12 : 6} key={name}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <SelectionMenuItem 
                    onClick={() => handleItemSelect(color)}
                    selected={isColorSelected(color)}
                  >
                    {image ? (
                      <SelectionMenuPreview sx={{ backgroundImage: `url(${image})` }} />
                    ) : type ? (
                      renderToolPreview(type, '32px')
                    ) : (
                      <SelectionMenuPreview />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                      <SelectionMenuText>{name}</SelectionMenuText>
                      {description && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary', 
                            fontSize: '0.7rem',
                            fontStyle: 'italic',
                            lineHeight: 1.3
                          }}
                        >
                          {description}
                        </Typography>
                      )}
                    </Box>
                  </SelectionMenuItem>
                </Box>
              </MuiGrid>
            );
          })}
        </MuiGrid>
      </GGPopover>

      <ToolbarDivider />

      <Box sx={{ flexGrow: 1 }} />

      <Tooltip title="Clear Terrain" placement="bottom">
        <DangerIconButton onClick={onClearColors}>
          <LayersClearIcon />
        </DangerIconButton>
      </Tooltip>

      {showClearGrid && (
        <Tooltip title="Clear Grid" placement="bottom">
          <DangerIconButton onClick={onClearGrid}>
            <DeleteForeverIcon />
          </DangerIconButton>
        </Tooltip>
      )}
    </ContextualToolbar>
  );
};

export default PaintToolbar;

